import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { readAuthToken } from '@noor/api';
import { env, FIRST_PAGE } from '@noor/config';
import {
  resolveSystemUserSearchType,
  systemUserListSearchSchema,
  systemUserQueries,
  type SystemUserListSearch,
} from '@/entities/system-user';
import { SystemUsersPage } from '@/pages/system-users';

/**
 * System-users route — URL is the single source of truth for
 * search/role/status/page. Prefetch is best-effort and non-blocking; the
 * page renders its shell immediately and the table widget surfaces
 * loading/error.
 */
export const Route = createFileRoute('/users')({
  staticData: { permission: 'users.view' },
  beforeLoad: () => {
    if (!env.VITE_ENABLE_MSW && !readAuthToken()) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login' });
    }
  },
  validateSearch: systemUserListSearchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    const limit = deps.pageSize;
    void context.queryClient
      .prefetchQuery(
        systemUserQueries.list({
          // Same shape as the table's query key: the search pair rides only
          // when a value is set.
          ...(deps.search_value
            ? {
                searchType: resolveSystemUserSearchType(deps.search_type),
                searchValue: deps.search_value,
              }
            : {}),
          role: deps.role,
          status: deps.status,
          limit,
          offset: (deps.page - 1) * limit,
        }),
      )
      .catch(() => undefined);
  },
  component: SystemUsersRoute,
});

function SystemUsersRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const onParamsChange = (next: Partial<SystemUserListSearch>) => {
    const resetPage = !('page' in next) && search.page !== FIRST_PAGE;
    void navigate({
      search: (prev) => ({
        ...prev,
        ...next,
        ...(resetPage ? { page: FIRST_PAGE } : {}),
      }),
    });
  };

  return <SystemUsersPage params={search} onParamsChange={onParamsChange} />;
}
